/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable @typescript-eslint/comma-dangle */
/* eslint-disable react/no-unknown-property */
import * as Yup from 'yup';
import {
  Action,
  ActionIcon,
  Card, HStack, ICON_ARROW_UP, Message, Spacing, StepNavigation, StepNavigationVariant, Text, useCarousel,
  useNotifications,
} from '@doveit/bricks';
import { inRange } from 'lodash';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import objectHash from 'object-hash';
import * as styles from './Wizard.style';
import Divider from '../../components/divider/Divider';
import {
  WizardStepValidationStatus, WizardProps, WizardStepFormRef, WizardStepPosition
} from './types';

const Wizard = <TData,>({
  initialData,
  steps,
  validationStrategy,
  onFinish,
  ...rest
}: WizardProps<TData>) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { addError } = useNotifications();
  const [data, setData] = React.useState(initialData);

  const { activePosition = 0, jump } = useCarousel({
    totalElements: steps.length,
  });

  const [shouldGoToStep, setShouldGoToStep] = React.useState<WizardStepPosition>(activePosition);
  const stepRef = React.useRef<WizardStepFormRef>() as React.MutableRefObject<WizardStepFormRef>;
  const nextStepRef = React.useRef<WizardStepPosition>(activePosition);

  const [stepsValidationStatus, setStepsValidationStatus] = React.useState(
    () => steps.reduce<Record<number, WizardStepValidationStatus>>((acc, _, step) => ({
      ...acc,
      [step]: { hasError: false },
    }), {})
  );

  /**
   * Reinitialize `stepsValidationStatus` every time `steps` change.
   */
  React.useEffect(() => {
    setStepsValidationStatus(steps.reduce<Record<number, WizardStepValidationStatus>>((acc, _, step) => ({
      ...acc,
      [step]: { hasError: false },
    }), {}));
  }, [steps]);

  const navigationItems = React.useMemo(() => steps.map(({ navigation }, i) => ({
    ...navigation,
    hasError: stepsValidationStatus[i].hasError,
  })), [steps, stepsValidationStatus]);

  const hasAtLeastOneError = React.useMemo(
    () => Object.values(stepsValidationStatus).some(({ hasError }) => hasError),
    [stepsValidationStatus],
  );

  const currentStep = steps[activePosition];

  /**
   * Returns the next enabled step in the wizard, skipping any steps marked as disabled.
   *
   * @param current - The current step index.
   * @param direction - Direction to search: 1 for next, -1 for previous. Defaults to 1.
   * @returns The index of the next enabled step, or 'completed' if moving forward past the last step, or 0 if moving backward past the first step.
   */
  const getNextEnabledStep = React.useCallback((current: number, direction: 1 | -1 = 1): WizardStepPosition => {
    let next = current + direction;
    while (next >= 0 && next < steps.length) {
      if (!steps[next].navigation.disabled) return next;
      next += direction;
    }
    return direction === 1 ? 'completed' : 0;
  }, [steps]);

  /**
   * Handles submitting the current step (if required) and navigating to the specified step.
   *
   * @param shouldSubmit - Whether to submit the current step before navigating.
   * @param nextPosition - The step index or 'completed' to navigate to next.
   */
  const shouldSubmitAndGoToStep = React.useCallback((shouldSubmit: boolean, nextPosition: WizardStepPosition) => {
    if (stepRef.current && shouldSubmit) {
      const formEvent = {} as unknown as React.FormEvent<HTMLFormElement>;
      stepRef?.current?.handleSubmit(formEvent);

      nextStepRef.current = nextPosition;
      return;
    }

    setShouldGoToStep(nextPosition);
  }, []);

  /**
   * Navigates to the desired step whenever `shouldGoToStep` changes,
   * skipping disabled steps. Calls `onFinish` if the wizard is completed.
   */
  React.useEffect(() => {
    let nextPosition = shouldGoToStep;

    if (typeof nextPosition === 'number') {
      nextPosition = getNextEnabledStep(nextPosition - 1);
    }

    nextStepRef.current = nextPosition;

    if (nextPosition === 'completed') {
      // @todo find a better way to fix the type
      onFinish?.(data as TData);
    } else {
      navigate(`${location.pathname}#${nextPosition + 1}`);
    }
  }, [data, getNextEnabledStep, location.pathname, navigate, onFinish, shouldGoToStep]);

  /**
   * Initializes the wizard to the step indicated in the URL hash, if valid.
   * Defaults to the first step otherwise.
   */
  React.useEffect(() => {
    const hashStep = Number(location.hash.substring(1));

    if (!Number.isNaN(hashStep) && inRange(hashStep, 1, steps.length + 1)) {
      jump?.(hashStep - 1);
    } else {
      jump?.(0);
    }
  }, [jump, location.hash, location.pathname, steps.length]);

  /**
   * Validates the provided wizard data against the step validation schemas.
   *
   * - Uses either a single-step ('progressive') or full validation strategy.
   * - Updates each step's validation status in state.
   *
   * @param dataToValidate - Partial wizard data to validate.
   * @returns `true` if any validation errors were found, otherwise `false`.
   */
  const validateData = React.useCallback(async (dataToValidate: Partial<TData>): Promise<boolean> => {
    const errors: Yup.ValidationError[] = [];

    const stepsToValidate = validationStrategy === 'progressive'
      ? [currentStep]
      : steps;

    await Promise.all(stepsToValidate.map(async ({ validationSchema }, step) => {
      try {
        await validationSchema?.validate(dataToValidate, { abortEarly: false });

        setStepsValidationStatus((prev) => ({
          ...prev,
          [step]: {
            hasError: false,
          },
        }));
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          errors.push(err);

          const additionalInfo = err.inner.reduce<Record<string, string>>((acc, { path, message }) => {
            if (path) {
              return {
                ...acc,
                [path]: message,
              };
            }

            return acc;
          }, {});

          setStepsValidationStatus((prev) => ({
            ...prev,
            [step]: {
              hasError: true,
              additionalInfo,
            },
          }));
        }
      }
    }));

    return errors.length > 0;
  }, [currentStep, steps, validationStrategy]);

  /**
   * Handles submission of the current step’s data.
   *
   * - Performs validation based on the wizard’s `validationStrategy`.
   * - Calls the step’s `onSubmit` handler if the data is dirty.
   * - Updates wizard state (`data`, `isSubmitting`) and navigates to the next step.
   *
   * @param stepData - Partial data from the current step.
   * @param isDirty - Whether the step data has changed since last submission.
   */
  const onSubmit = React.useCallback(async (stepData: Partial<TData>, isDirty: boolean) => {
    if (validationStrategy === 'progressive' || (validationStrategy === 'at-completion' && nextStepRef.current === 'completed')) {
      const hasAtLeastAnError = await validateData(stepData);
      if (hasAtLeastAnError) {
        return;
      }
    }

    if (isDirty) {
      setIsSubmitting(true);

      try {
        const submittedData = await steps[activePosition].onSubmit?.(stepData);

        setData(submittedData);

        setIsSubmitting(false);
      } catch (err) {
        if (err instanceof Error) {
          addError(err.message);
        }

        setIsSubmitting(false);
        return;
      }
    }

    setShouldGoToStep(nextStepRef.current);
  }, [activePosition, addError, steps, validateData, validationStrategy]);

  return (
    <div {...rest}>
      <HStack
        css={styles.wizardNavigation}
        justifyContent="space-between"
        alignItems="center"
        gap={100}
      >
        <StepNavigation
          items={navigationItems}
          onChange={(pos) => shouldSubmitAndGoToStep(true, pos)}
          variant={StepNavigationVariant.COMPACT}
          currentPosition={activePosition}
        />
      </HStack>

      <Spacing margin={[200, 0, 0]} />

      <Card paddingSize="M">
        <Card.Header>
          <Text.H4>
            {activePosition + 1}. {currentStep.navigation.title}
          </Text.H4>
        </Card.Header>

        <Card.Content>
          {stepsValidationStatus[activePosition].hasError && (
            <Spacing margin={[0, 0, 300]}>
              <Message
                type="critical"
                message="È necessario risolvere i seguenti errori prima di poter procedere"
              >
                <ul>
                  {Object.values(stepsValidationStatus[activePosition].additionalInfo).map((message) => (
                    <li key={message}>
                      {message}
                    </li>
                  ))}
                </ul>
              </Message>
            </Spacing>
          )}

          {currentStep.render({
            formRef: stepRef,
            initialData: data,
            onSubmit,
            loading: isSubmitting,
          })}

          {validationStrategy === 'at-completion' && activePosition === steps.length - 1 && hasAtLeastOneError && (
            <>
              <Divider />
              <div aria-label="Elenco degli errori di validazione">
                <Message
                  type="critical"
                  message="Non è stato possibile procedere. Alcuni step risultano incompleti."
                />

                <Spacing margin={[400, 0, 0, 0]}>
                  <strong>Correggi i seguenti step prima di poter procedere:</strong>

                  <Spacing margin={[200, 0, 0, 0]}>
                    {steps.map(({ navigation }, j) => {
                      if (stepsValidationStatus[j].hasError) {
                        const humanStepNumber = j + 1;

                        return (
                          <div
                            key={objectHash([j, navigation.title])}
                            css={styles.erroredStepListItem}
                            onClick={() => navigate(`${location.pathname}#${j + 1}`)}
                            aria-label={`Correggi i dati dello step ${humanStepNumber}`}
                            role="button"
                          >
                            <div>{humanStepNumber}. {navigation.title}</div>
                            <strong>Correggi</strong>
                          </div>
                        );
                      }

                      return null;
                    })}
                  </Spacing>
                </Spacing>
              </div>
            </>
          )}
        </Card.Content>

        <Card.Divider />

        <Card.Content>
          <HStack justifyContent="space-between">
            <div>
              {activePosition !== 0 && typeof currentStep.renderPrevStepAction !== 'boolean' && (
                currentStep.renderPrevStepAction?.({
                  isSubmitting,
                  goToPreviousStep: (submitCurrentStep: boolean = true) => shouldSubmitAndGoToStep(submitCurrentStep, activePosition - 1)
                }) || (
                  <ActionIcon
                    aria-label="Salva i dati e torna allo step precedente"
                    label="Salva i dati e torna allo step precedente"
                    onClick={() => shouldSubmitAndGoToStep(true, activePosition - 1)}
                    disabled={isSubmitting}
                    icon={{
                      path: ICON_ARROW_UP,
                      direction: 'left',
                    }}
                  />
                )
              )}
            </div>

            <HStack>
              {typeof currentStep.renderSkipStepAction !== 'boolean' && (
                currentStep.renderSkipStepAction?.({
                  currentStep: activePosition,
                  isSubmitting,
                  skipStep: (goTo, submitCurrentStep = false) => shouldSubmitAndGoToStep(
                    submitCurrentStep,
                    goTo,
                  )
                }) || (
                  <Action
                    emphasis="high"
                    label={activePosition < steps.length - 1 ? 'Salta' : 'Salta ed esci'}
                    aria-label="Salta lo step e procedi allo step successivo"
                    onClick={() => shouldSubmitAndGoToStep(
                      false,
                      activePosition < steps.length - 1 ? activePosition + 1 : 'completed'
                    )}
                    disabled={isSubmitting}
                  />
                )
              )}

              {typeof currentStep.renderNextStepAction !== 'boolean' && (
                currentStep.renderNextStepAction?.({
                  isSubmitting,
                  goToNextStep: (submitCurrentStep: boolean = true) => shouldSubmitAndGoToStep(
                    submitCurrentStep,
                    activePosition < steps.length - 1 ? activePosition + 1 : 'completed'
                  )
                }) || (
                  <Action
                    label="Salva e continua"
                    aria-label="Salva i dati e procedi allo step successivo"
                    color="primary"
                    emphasis="high"
                    onClick={() => shouldSubmitAndGoToStep(
                      true,
                      activePosition < steps.length - 1 ? activePosition + 1 : 'completed'
                    )}
                    loading={isSubmitting}
                    iconRight={{
                      path: ICON_ARROW_UP,
                      direction: 'right',
                    }}
                  />
                )
              )}
            </HStack>
          </HStack>
        </Card.Content>
      </Card>
    </div>
  );
};

export default Wizard;
